import React, { useState, createContext, useEffect, useRef } from "react";

import { Modal } from "antd";

import { readDraft, writeDraft } from "./draft";

const { confirm } = Modal;

export const AddOrderDeliveryContext = createContext();

/**
 * Агуулах/салбарын блокийн id. Өмнө нь `new Date().getTime()` байсан тул нэг
 * рендерт үүсэх агуулах ба салбарын блок ижил id авч, формын талбарууд
 * (`wh-code-<id>`) хоорондоо хутгалддаг байсан.
 */
let blockCounter = 0;
const nextBlockId = (prefix) => `${prefix}-${new Date().getTime()}-${++blockCounter}`;

/**
 * @param {string} [props.draftKey] sessionStorage-ийн key. Дамжуулбал бөглөсөн
 * мэдээлэл refresh хийхэд сэргэнэ. Дамжуулаагүй бол (шуурхай хүргэлтийн урсгал)
 * хадгалахгүй — хоёр урсгалын draft хоорондоо хутгалдахгүй.
 */
export const AddOrderDeliveryProvider = ({ draftKey, children }) => {
  // Хуудас refresh хийхэд бөглөсөн мэдээлэл сэргэхийн тулд эхлэхдээ л уншина
  const draft = useRef(readDraft(draftKey)).current;

  const [reference, setReference] = useState(draft.reference ?? {});
  const [selectedWareHouses, setSelectedWareHouses] = useState(draft.selectedWareHouses ?? []);
  const [selectedBranches, setSelectedBranches] = useState(draft.selectedBranches ?? []);
  // Хоёр эх сурвалж хоёулаа анхнаасаа нээлттэй. Хэрэглэгч хаасан бол `false`
  // нь draft-д хадгалагдана — Step1 дээр дахин хүчээр нээхгүй.
  const [fromBranch, setFromBranch] = useState(draft.fromBranch ?? true);
  const [fromWarehouse, setFromWareHouse] = useState(draft.fromWarehouse ?? true);
  const [customFields, setCustomFields] = useState([]);
  const [bagHorooId, setBagHorooId] = useState(draft.bagHorooId);
  const [addressDetail, setAddressDetail] = useState(draft.addressDetail ?? "");
  const [addressArray, setAddressArray] = useState(draft.addressArray ?? []);
  const [selectedCustomerField, setSelectedCustomerField] = useState(draft.selectedCustomerField);
  const [formDataStep1, setFormDataStep1] = useState(draft.formDataStep1);
  const [formDataStep2, setFormDataStep2] = useState(draft.formDataStep2);
  // Step2 дээр бөглөж байгаа (хараахан "Үргэлжлүүлээгүй") формын утгууд —
  // refresh хийхэд нэр, утас, дэлгэрэнгүй хаяг зэрэг нь сэргэнэ.
  const [formValuesStep2, setFormValuesStep2] = useState(draft.formValuesStep2);
  // Step1 дээр сонгосон төлбөрийн нөхцөл, гэрээний төрөл, агуулахын код.
  // Step1 нь алхам солиход unmount болдог тул эдгээрийг контекстэд хадгална —
  // эс бөгөөс "Буцах" дарахад бараа сонгох блокууд алга болдог.
  const [solvedPrice, setSolvedPrice] = useState(draft.solvedPrice);
  const [usedContractType, setUsedContractType] = useState(draft.usedContractType);
  const [warehouseCode, setWarehouseCode] = useState(draft.warehouseCode);
  const [selectedTab, setSelectedTab] = useState(draft.selectedTab ?? "addressMap");
  const [selectedDeliveryType, setSelelctedDeliveryType] = useState(
    draft.selectedDeliveryType ?? ""
  );
  const [selectedDeliveryTime, setSelelctedDeliveryTime] = useState(
    draft.selectedDeliveryTime ?? null
  );
  const [selectedBox, setSelectedBox] = useState(draft.selectedBox ?? null);
  const [committee, setCommittee] = useState(draft.committee ?? null);
  const [selectedLocker, setSelectedLocker] = useState(draft.selectedLocker ?? {});
  const [lockers, setLockers] = useState([]);
  // Газрын зураг дээрх сонголтын жагсаалт. `selectedDeliveryAddress`-ыг энэ
  // жагсаалттай тулган харуулдаг тул хоёуланг нь хамт хадгална.
  const [deliveryAddress, setDeliveryAddress] = useState(draft.deliveryAddress ?? []);
  const [selectedDeliveryAddress, setSelectedDeliveryAddress] = useState(
    draft.selectedDeliveryAddress ?? { location: { lat: 47.9123565, lon: 106.9151432 } }
  );

  const [warehouseList, setWareHouseList] = useState(
    draft.warehouseList ?? [{ id: nextBlockId('wh'), products: [] }]
  );
  const [minStockNotifiedProducts, setMinStockNotifiedProducts] = useState([]);

  const [branchList, setBranchList] = useState(
    draft.branchList ?? [{ id: nextBlockId('br'), products: [] }]
  );

  // Төлөв өөрчлөгдөх бүрд draft-ыг шинэчилнэ. `lockers`, `customFields` нь
  // API-аас дахин татагддаг тул хадгалахгүй.
  useEffect(() => {
    writeDraft(draftKey, {
      reference,
      selectedWareHouses,
      selectedBranches,
      fromBranch,
      fromWarehouse,
      bagHorooId,
      addressDetail,
      addressArray,
      selectedCustomerField,
      formDataStep1,
      formDataStep2,
      formValuesStep2,
      solvedPrice,
      usedContractType,
      warehouseCode,
      selectedTab,
      selectedDeliveryType,
      selectedDeliveryTime,
      selectedBox,
      committee,
      selectedLocker,
      deliveryAddress,
      selectedDeliveryAddress,
      warehouseList,
      branchList,
    });
  }, [
    draftKey,
    reference,
    selectedWareHouses,
    selectedBranches,
    fromBranch,
    fromWarehouse,
    bagHorooId,
    addressDetail,
    addressArray,
    selectedCustomerField,
    formDataStep1,
    formDataStep2,
    formValuesStep2,
    solvedPrice,
    usedContractType,
    warehouseCode,
    selectedTab,
    selectedDeliveryType,
    selectedDeliveryTime,
    selectedBox,
    committee,
    selectedLocker,
    deliveryAddress,
    selectedDeliveryAddress,
    warehouseList,
    branchList,
  ]);

  const addWarehouse = () => {
    setWareHouseList([
      ...warehouseList,
      { id: nextBlockId('wh'), products: [] },
    ]);
  };

  const removeWarehouse = (id) => {
    confirm({
      title: "Агуулахаас татахаар сонгосон бараануудыг устгах уу?",
      okText: "Тийм",
      cancelText: "Үгүй",
      okButtonProps: {
        style: { backgroundColor: "#2FA1D9" },
      },
      onOk: () => {
        let tmp = [...warehouseList];
        setWareHouseList(tmp.filter((item) => item.id !== id));

        let tmp1 = [...selectedWareHouses];
        setSelectedWareHouses(tmp1.filter((item) => item.id !== id));
      },
    });
  };

  const addProductWarehouse = (id, products = []) => {
    let tmp = [...warehouseList];
    tmp = tmp.map((item) => {
      if (item.id === id) item.products = products;
      return item;
    });
    setWareHouseList(tmp);
  };

  const removeProductWarehouse = (wareHouseId, productId) => {
    let tmp = [...warehouseList];
    tmp = tmp.map((item) => {
      if (item.id === wareHouseId) {
        if (item.products.length) {
          const pIndex = item.products.findIndex((el) => el._id === productId);
          if (pIndex !== -1) {
            item.products.splice(pIndex, 1);
          }
        }
      }
      return item;
    });
    setWareHouseList(tmp);
  };

  const removeProductBranch = (branchId, productId) => {
    let tmp = [...branchList];
    tmp = tmp.map((item) => {
      if (item.id === branchId) {
        if (item.products.length) {
          const pIndex = item.products.findIndex((el) => el._id === productId);
          if (pIndex !== -1) {
            item.products.splice(pIndex, 1);
          }
        }
      }
      return item;
    });
    setBranchList(tmp);
  };

  const updateWarehouseProducts = (id, products) => {
    const wareHouseIndex = warehouseList.findIndex((obj) => obj.id === id);
    warehouseList[wareHouseIndex].products = products;
    setWareHouseList([...warehouseList]);
  };

  const onChangeProductUnitWareHouse = (id, prodIndex, unit) => {
    let tmp = [...warehouseList];
    tmp = tmp.map((item) => {
      if (item.id === id) {
        if (item?.products?.length > prodIndex) {
          item.products[prodIndex].totalUnit = unit;
        }
      }
      return item;
    });
    setWareHouseList(tmp);
  };

  const onChangeProductUnitBranch = (id, prodIndex, unit) => {
    let tmp = [...branchList];
    tmp = tmp.map((item) => {
      if (item.id === id) {
        if (item?.products?.length > prodIndex) {
          item.products[prodIndex].totalUnit = unit;
        }
      }
      return item;
    });
    setBranchList(tmp);
  };

  const addBranch = () => {
    setBranchList([
      ...branchList,
      {
        id: nextBlockId('br'),
        products: [],
      },
    ]);
  };

  const removeBranch = (id) => {
    confirm({
      title: "Салбараас татахаар сонгосон бараануудыг устгах уу?",
      okText: "Тийм",
      cancelText: "Үгүй",
      okButtonProps: {
        style: { backgroundColor: "#2FA1D9" },
      },
      onOk: () => {
        let tmp = [...branchList];
        setBranchList(tmp.filter((item) => item.id !== id));

        let tmp1 = [...selectedBranches];
        setSelectedBranches(tmp1.filter((item) => item.id !== id));
      },
    });
  };

  const addProductBranch = (id, products = []) => {
    let tmp = [...branchList];
    tmp = tmp.map((item) => {
      if (item.id === id) {
        // item.products = [...item.products, ...products];
        item.products = products;
      }
      return item;
    });
    setBranchList(tmp);
  };

  const addSelectedWareHouse = (warehouse = { id: "", value: "" }) => {
    if (selectedWareHouses.find((wh) => wh?.id === warehouse.id)) {
      let tmp = selectedWareHouses.map((item) => {
        if (item.id === warehouse.id) {
          item.value = warehouse.value;
        }
        return item;
      });

      setSelectedWareHouses([...tmp]);
    } else {
      setSelectedWareHouses([...selectedWareHouses, warehouse]);
    }
  };

  const clearWareHouseProducts = (id = "") => {
    let tmp = [...warehouseList];
    tmp = tmp.map((item) => {
      if (item.id === id) {
        item.products = [];
      }
      return item;
    });
    setWareHouseList(tmp);
  };

  const addSelectedBranches = (branch = { id: "", value: "" }) => {
    if (selectedBranches.find((wh) => wh?.id === branch.id)) {
      let tmp = selectedBranches.map((item) => {
        if (item.id === branch.id) {
          item.value = branch.value;
        }
        return item;
      });
      setSelectedBranches([...tmp]);
    } else {
      setSelectedBranches([...selectedBranches, branch]);
    }
  };

  const clearSelectedWareHouses = () => setSelectedWareHouses([]);
  const clearSelectedBranches = () => setSelectedBranches([]);

  return (
    <AddOrderDeliveryContext.Provider
      value={{
        // Захиалга үүссэний дараа Step3 нь ЯГ энэ key-г цэвэрлэнэ
        draftKey,
        addressDetail,
        setAddressDetail,
        reference,
        setReference,
        selectedWareHouses,
        addSelectedWareHouse,
        clearSelectedWareHouses,
        selectedBranches,
        addSelectedBranches,
        clearSelectedBranches,
        selectedTab,
        setSelectedTab,
        selectedDeliveryType,
        setSelelctedDeliveryType,
        selectedDeliveryTime,
        setSelelctedDeliveryTime,
        selectedBox,
        setSelectedBox,
        committee,
        setCommittee,
        selectedLocker,
        setSelectedLocker,
        lockers,
        setLockers,
        deliveryAddress,
        setDeliveryAddress,
        selectedDeliveryAddress,
        setSelectedDeliveryAddress,

        branchList,
        setBranchList,
        addBranch,
        removeBranch,
        addProductBranch,
        removeProductBranch,
        selectedCustomerField,
        setSelectedCustomerField,
        warehouseList,
        setWareHouseList,
        minStockNotifiedProducts,
        setMinStockNotifiedProducts,
        addWarehouse,
        removeWarehouse,
        addProductWarehouse,
        clearWareHouseProducts,
        removeProductWarehouse,

        onChangeProductUnitWareHouse,
        onChangeProductUnitBranch,
        updateWarehouseProducts,

        formDataStep1,
        formDataStep2,
        setFormDataStep1,
        setFormDataStep2,
        formValuesStep2,
        setFormValuesStep2,

        solvedPrice,
        setSolvedPrice,
        usedContractType,
        setUsedContractType,
        warehouseCode,
        setWarehouseCode,

        fromBranch,
        setFromBranch,

        fromWarehouse,
        setFromWareHouse,

        customFields,
        setCustomFields,

        addressArray,
        setAddressArray,

        bagHorooId,
        setBagHorooId,
      }}
    >
      {children}
    </AddOrderDeliveryContext.Provider>
  );
};
